import { ChevronDownIcon, ChevronRightIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";

export interface AccordionSectionProps {
  /** Section label/title */
  label: string;
  /** Number of active selections in this section */
  activeCount: number;
  /** Whether the section is expanded */
  isExpanded: boolean;
  /** Toggle expand/collapse callback */
  onToggle: () => void;
  /** Section content (rendered when expanded) */
  children: React.ReactNode;
  /** Whether the entire section is disabled */
  disabled?: boolean;
  /** Helper text shown below the header */
  helperText?: string;
}

export default function AccordionSection({
  label,
  activeCount,
  isExpanded,
  onToggle,
  children,
  disabled = false,
  helperText,
}: AccordionSectionProps) {
  return (
    <div
      className={clsx(
        "transition-colors",
        disabled && "opacity-40"
      )}
    >
      {/* Header */}
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        className={clsx(
          "w-full px-3 py-2 flex items-center gap-2 text-left focus:outline-none transition-colors",
          disabled
            ? "cursor-not-allowed"
            : "hover:bg-gray-800/50 cursor-pointer"
        )}
      >
        {/* Chevron */}
        {isExpanded ? (
          <ChevronDownIcon className="size-3 text-gray-400 flex-shrink-0" />
        ) : (
          <ChevronRightIcon className="size-3 text-gray-400 flex-shrink-0" />
        )}

        {/* Label */}
        <span
          className={clsx(
            "text-xs font-medium flex-1 transition-colors",
            disabled ? "text-gray-500" : isExpanded ? "text-gray-100" : "text-gray-400"
          )}
        >
          {label}
        </span>

        {/* Active count badge */}
        {activeCount > 0 && (
          <span
            // Grey, not blue. Blue was doing four jobs in this panel — counts,
            // checked boxes, Done, links — so it had stopped meaning any of
            // them. It now means "selected" and nothing else.
            className="px-1.5 py-0.5 text-2xs font-medium text-gray-400"
          >
            {activeCount}
          </span>
        )}
      </button>

      {/* Helper text */}
      {helperText && !isExpanded && (
        <div className="px-3 pb-1.5 -mt-0.5">
          <span className="text-2xs text-gray-500">{helperText}</span>
        </div>
      )}

      {/* Content */}
      {isExpanded && (
        <div className="px-3 pb-2">
          {helperText && (
            <div className="mb-1.5">
              <span className="text-2xs text-gray-500">{helperText}</span>
            </div>
          )}
          {children}
        </div>
      )}
    </div>
  );
}
